import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react'
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom'
import {
  Button,
  EmptyState,
  SearchWithFilter,
  Tabs,
  type FilterSelection,
  type TabItem,
} from '@enamelai/design-system'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import PendingFormsTable from '@components/pages/FormBuilder/PendingFormsTable'
import CompletedFormsTable from '@components/pages/FormBuilder/CompletedFormsTable'
import { getPendingFormEntries } from '@services/api/forms.api'
import { getFormsSocket } from '@services/websocket/socket'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import SendFormsTab from './SendFormsTab'
import PatientFormsView from './PatientFormsView.page'
import CompletedFormsView from './CompletedFormsView.page'
import Templates from './Templates.page'
import CreateForm from './CreateForm.page'
import type { CompletedFormsViewLocationState } from './CompletedFormsView.page'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import {
  mergeAssignedFormEntry,
  reconcilePendingEntries,
} from '@pages/FormBuilder/createAssignedFormEntry'
import { fetchAllContacts } from '@store/actions'
import { openKioskWindow } from '@utils/kioskFormsUrl.util'
import FormBuilderSkeleton from './FormBuilderSkeleton.page'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import './FormBuilder.css'
import SendPatientFormsModal from '@components/pages/FormBuilder/SendingFormsTable/SendPatientFormsModal.component'
import {
  countFormRequests,
  filterFormRequestSources,
} from './formRequests.util'

type TabType = 'pending' | 'send' | 'completed'
type ViewMode =
  | 'dashboard'
  | 'templates'
  | 'create'
  | 'patientView'
  | 'completedDetail'

/** Toggle to show the "Open Kiosk" link in the Form Builder header. */
const SHOW_FORM_BUILDER_KIOSK_LINK = false
const TAB_SKELETON_DURATION_MS = 500
const TAB_ITEMS: TabItem[] = [
  { id: 'pending', label: 'Pending forms' },
  { id: 'send', label: 'Send forms' },
  { id: 'completed', label: 'Completed forms' },
]
const FORM_BUILDER_LOADING_CONTAINER = '.form-builder-page'

const FormBuilder: React.FC = () => {
  const dispatch = useAppDispatch()
  const selectedStorePatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const selectedPatientId = selectedStorePatient?.patient_id ?? null
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [activeTab, setActiveTab] = useState<TabType>('pending')
  const [isTabSkeleton, setIsTabSkeleton] = useState(false)
  const [tabSkeletonKey, setTabSkeletonKey] = useState(0)
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const firstLoadStartedAtRef = useRef(Date.now())
  const prevTabRef = useRef<TabType>('pending')
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard')
  const [selectedPatient, setSelectedPatient] = useState<{
    patientId: string
    patientName?: string
  } | null>(null)
  const [completedDetailPayload, setCompletedDetailPayload] =
    useState<CompletedFormsViewLocationState | null>(null)
  const [subviewReturnTab, setSubviewReturnTab] = useState<TabType>('pending')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})
  const [pendingFormsData, setPendingFormsData] = useState<PendingFormEntry[]>(
    [],
  )
  const [pendingFormsLoading, setPendingFormsLoading] = useState<boolean>(false)
  // Patient row to auto-expand on the Pending tab — set from a deep-link
  // (e.g. "Forms pending before appointment" notification carries
  // `{ tab: 'pending', patientId }` in `location.state`).
  const [pendingExpandedPatientId, setPendingExpandedPatientId] = useState<
    string | null
  >(null)
  // Patient row to highlight on the Completed tab — set from a "Form
  // completed" notification deep-link (`{ tab: 'completed', patientId,
  // assignmentId }`). The completed table has no expandable rows, so the
  // analogue of pending's "expand" is row selection.
  const [completedSelectedPatientId, setCompletedSelectedPatientId] = useState<
    string | null
  >(null)
  const [sendNoRecordsEmpty, setSendNoRecordsEmpty] = useState(false)
  const [sendFormsLoading, setSendFormsLoading] = useState(false)
  const [showSendForm, setShowSendForm] = useState(false)

  useEffect(() => {
    void dispatch(fetchAllContacts())
  }, [dispatch])

  const pendingHasLoadedRef = useRef(false)
  const pendingFetchIdRef = useRef(0)

  useEffect(() => {
    pendingHasLoadedRef.current = false
  }, [selectedPatientId])

  const fetchPendingForms = useCallback(
    (options?: { showGlobalLoading?: boolean; silent?: boolean }): void => {
      const silent = options?.silent ?? false
      const showGlobalLoading =
        (options?.showGlobalLoading ?? true) && !isPageReadyRef.current
      const requestId = ++pendingFetchIdRef.current
      const startedAt = Date.now()
      if (!silent) {
        setPendingFormsLoading(true)
      }
      if (showGlobalLoading) {
        showLoading({
          container: FORM_BUILDER_LOADING_CONTAINER,
          style: FIRST_LOAD_OVERLAY_STYLE,
        })
      }
      getPendingFormEntries(
        selectedPatientId ? { patient_id: selectedPatientId } : undefined,
      )
        .then((entries) => {
          if (requestId !== pendingFetchIdRef.current) return
          pendingHasLoadedRef.current = true
          setPendingFormsData((prev) => {
            const local = selectedPatientId
              ? prev.filter((entry) => entry.patientId === selectedPatientId)
              : prev
            return reconcilePendingEntries(entries, local)
          })
        })
        .catch(() => {
          if (requestId !== pendingFetchIdRef.current) return
          setPendingFormsData((prev) => prev)
          showToast(
            createFailedToast(
              'Error',
              'Failed to load pending forms. Please try again.',
            ),
          )
        })
        .finally(() => {
          void (async (): Promise<void> => {
            if (showGlobalLoading) {
              await waitAtLeast(
                firstLoadStartedAtRef.current,
                FIRST_LOAD_OVERLAY_MS,
              )
            } else if (!silent) {
              await waitAtLeast(startedAt, TAB_SKELETON_DURATION_MS)
            }
            if (requestId !== pendingFetchIdRef.current) return
            if (!silent) {
              setPendingFormsLoading(false)
            }
            if (showGlobalLoading) {
              isPageReadyRef.current = true
              setIsPageReady(true)
              hideLoading(FORM_BUILDER_LOADING_CONTAINER)
            }
          })()
        })
    },
    [hideLoading, selectedPatientId, showLoading, showToast],
  )

  const handleFormsAssigned = useCallback(
    (entry: PendingFormEntry): void => {
      if (selectedPatientId && entry.patientId !== selectedPatientId) {
        return
      }
      pendingHasLoadedRef.current = true
      setPendingFormsData((prev) => mergeAssignedFormEntry(prev, entry))
      fetchPendingForms({ showGlobalLoading: false, silent: true })
    },
    [fetchPendingForms, selectedPatientId],
  )

  useLayoutEffect(() => {
    firstLoadStartedAtRef.current = Date.now()
    showLoading({
      container: FORM_BUILDER_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  useEffect(() => {
    const isDashboard =
      location.pathname === '/form-builder' ||
      location.pathname === '/form-builder/'
    if (!isDashboard) return
    if (activeTab !== 'pending' && activeTab !== 'completed') return
    const pageReady = isPageReadyRef.current
    const alreadyLoaded = pendingHasLoadedRef.current
    fetchPendingForms({
      showGlobalLoading: !pageReady && !alreadyLoaded,
      silent: pageReady && alreadyLoaded,
    })
  }, [activeTab, fetchPendingForms, location.pathname])

  useEffect(() => {
    if (activeTab === 'pending' || activeTab === 'completed') return
    if (isPageReadyRef.current) return
    if (sendFormsLoading) return
    let cancelled = false
    void waitAtLeast(firstLoadStartedAtRef.current, FIRST_LOAD_OVERLAY_MS).then(
      () => {
        if (cancelled || isPageReadyRef.current) return
        isPageReadyRef.current = true
        setIsPageReady(true)
        hideLoading(FORM_BUILDER_LOADING_CONTAINER)
      },
    )
    return () => {
      cancelled = true
    }
  }, [activeTab, hideLoading, sendFormsLoading])

  useEffect(() => {
    return () => {
      hideLoading(FORM_BUILDER_LOADING_CONTAINER)
    }
  }, [hideLoading])

  useEffect(() => {
    if (isPageReadyRef.current && prevTabRef.current !== activeTab) {
      setIsTabSkeleton(true)
      setTabSkeletonKey((key) => key + 1)
    }
    prevTabRef.current = activeTab
  }, [activeTab])

  useEffect(() => {
    if (!isTabSkeleton) return
    const timer = setTimeout(() => {
      setIsTabSkeleton(false)
    }, TAB_SKELETON_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isTabSkeleton, tabSkeletonKey])

  useEffect(() => {
    const socket = getFormsSocket()
    if (!socket) return

    const handleAssignmentCompleted = (): void => {
      fetchPendingForms({ showGlobalLoading: false, silent: true })
    }

    socket.on('forms.assignment.completed', handleAssignmentCompleted)
    return () => {
      socket.off('forms.assignment.completed', handleAssignmentCompleted)
    }
  }, [fetchPendingForms])

  // Deep-link handling via `location.state`:
  //  - `{ tab: 'pending', patientId }`  → from forms-pending notification.
  //    Activate the Pending Forms tab and pre-expand the matching patient
  //    row in PendingFormsTable. Stay on the dashboard; do NOT open the
  //    per-patient subview (that would replace the table with a detail view
  //    and the user wouldn't see the expand we just performed).
  //  - `{ tab: 'completed', patientId, assignmentId }` → from a "Form
  //    completed" notification. Activate the Completed Forms tab and
  //    highlight the matching patient row in CompletedFormsTable. The
  //    `assignmentId` is what discriminates this flow from the legacy
  //    real-time toast (see App.tsx), which navigates with the same shape
  //    minus `assignmentId` and expects the patient subview to open.
  //  - `{ tab: 'completed', patientId }` (legacy realtime toast) → switch to
  //    Completed and open the patient subview.
  //  - `{ patientId }` only (legacy fallback) → open the patient subview.
  useEffect(() => {
    const state = location.state as {
      tab?: TabType
      patientId?: string
      patientName?: string
      assignmentId?: string
    } | null
    if (!state) return

    if (state.tab === 'pending') {
      setActiveTab('pending')
      setViewMode('dashboard')
      setPendingExpandedPatientId(state.patientId ?? null)
      return
    }

    if (state.tab === 'completed' && state.assignmentId) {
      setActiveTab('completed')
      setViewMode('dashboard')
      setCompletedSelectedPatientId(state.patientId ?? null)
      return
    }

    if (state.tab === 'completed') {
      setActiveTab('completed')
    }
    if (state.patientId) {
      setSubviewReturnTab('completed')
      setSelectedPatient({
        patientId: state.patientId,
        patientName: state.patientName,
      })
      setViewMode('patientView')
    }
  }, [location.state])

  const goBackToDashboard = useCallback((options?: { tab?: TabType }): void => {
    setViewMode('dashboard')
    setSelectedPatient(null)
    setCompletedDetailPayload(null)
    if (options?.tab) setActiveTab(options.tab)
  }, [])

  const patientScopedData = selectedPatientId
    ? pendingFormsData.filter((entry) => entry.patientId === selectedPatientId)
    : pendingFormsData
  const scopedPendingData = filterFormRequestSources(
    patientScopedData,
    selectedFilters.Source ?? [],
  )
  const handleTabChange = (tab: string): void => {
    if (viewMode !== 'dashboard') {
      goBackToDashboard({ tab: tab as TabType })
      return
    }
    if (tab === activeTab) return
    setActiveTab(tab as TabType)
  }

  const handleFilterChange = (nextFilters: FilterSelection): void => {
    setSelectedFilters(nextFilters)
  }

  const clearAllFilters = (): void => {
    setSearchQuery('')
    setSelectedFilters({})
  }

  const openPatientForms = useCallback(
    (patientId: string, patientName?: string): void => {
      setSubviewReturnTab(activeTab)
      setSelectedPatient({ patientId, patientName })
      setViewMode('patientView')
    },
    [activeTab],
  )

  const openCompletedDetail = useCallback(
    (payload: CompletedFormsViewLocationState): void => {
      setSubviewReturnTab('pending')
      setCompletedDetailPayload(payload)
      setViewMode('completedDetail')
    },
    [],
  )

  const handleCreateForm = (): void => {
    navigate('/form-builder/templates')
  }

  // ---------- Filtering helpers ----------

  const matchesSearchPending = (entry: PendingFormEntry): boolean => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return true

    if (
      entry.patientName.toLowerCase().includes(q) ||
      entry.patientId.toLowerCase().includes(q) ||
      (entry.publicPatientId ?? '').toLowerCase().includes(q)
    ) {
      return true
    }

    for (const apt of entry.appointments) {
      if (
        apt.appointmentType.toLowerCase().includes(q) ||
        apt.roomNo.toLowerCase().includes(q) ||
        apt.appointmentDate.toLowerCase().includes(q)
      ) {
        return true
      }
      if (apt.incompleteForms) {
        if (apt.incompleteForms.some((f) => f.name.toLowerCase().includes(q))) {
          return true
        }
      }
      if (apt.completedForms) {
        if (apt.completedForms.some((f) => f.name.toLowerCase().includes(q))) {
          return true
        }
      }
    }

    return false
  }

  const hasPendingForms = (entry: PendingFormEntry): boolean =>
    entry.appointments.some(
      (apt) =>
        typeof apt.completedFormsCount === 'number' &&
        typeof apt.totalFormsCount === 'number' &&
        apt.completedFormsCount < apt.totalFormsCount,
    )

  const hasAllFormsCompleted = (entry: PendingFormEntry): boolean =>
    entry.appointments.every(
      (apt) =>
        typeof apt.completedFormsCount === 'number' &&
        typeof apt.totalFormsCount === 'number' &&
        apt.completedFormsCount === apt.totalFormsCount,
    )

  const matchesStatusFilterPending = (entry: PendingFormEntry): boolean => {
    const statusFilters = selectedFilters.Status || []
    if (statusFilters.length === 0) return true

    let match = false
    if (statusFilters.includes('Has Pending Forms') && hasPendingForms(entry)) {
      match = true
    }
    if (
      statusFilters.includes('All Forms Completed') &&
      hasAllFormsCompleted(entry)
    ) {
      match = true
    }
    return match
  }

  const filteredPendingData = scopedPendingData
    .filter((entry) => !hasAllFormsCompleted(entry))
    .filter(
      (entry) =>
        matchesSearchPending(entry) && matchesStatusFilterPending(entry),
    )

  const hasAnyCompleted = (entry: PendingFormEntry): boolean =>
    entry.appointments.some(
      (apt) =>
        (apt.completedFormsCount ?? 0) > 0 ||
        (apt.completedForms?.length ?? 0) > 0,
    )

  const getPatientMobileFromEntry = (
    entry: PendingFormEntry,
  ): string | undefined => {
    for (const appointment of entry.appointments) {
      const patientMobile = appointment.completedForms?.[0]?.fields
        ?.find((field) => field.section === 'Contact Information')
        ?.items.find((item) => item.label === 'Mobile/Telephone')?.value as
        | string
        | undefined
      if (patientMobile?.trim()) {
        return patientMobile.trim()
      }
    }
    return undefined
  }

  const completedEntries = scopedPendingData
    .filter((entry) => hasAnyCompleted(entry))
    .map((entry) => ({
      patientId: entry.patientId,
      publicPatientId: entry.publicPatientId ?? entry.patientId,
      patientName: entry.patientName,
      patientMobile: getPatientMobileFromEntry(entry),
      completedFormsCount: countFormRequests(entry, 'completed'),
    }))

  const matchesSearchSimple = (entry: {
    patientName: string
    patientId: string
    publicPatientId?: string
  }): boolean => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return true
    return (
      entry.patientName.toLowerCase().includes(q) ||
      entry.patientId.toLowerCase().includes(q) ||
      (entry.publicPatientId ?? '').toLowerCase().includes(q)
    )
  }

  const filteredCompletedData = completedEntries.filter((entry) =>
    matchesSearchSimple(entry),
  )

  // Filters config per tab (simple MVP)
  const filtersConfig: Record<string, string[]> =
    activeTab === 'pending'
      ? {
          Status: ['Has Pending Forms'],
          Source: ['Appointment', 'Direct'],
        }
      : activeTab === 'completed'
        ? { Source: ['Appointment', 'Direct'] }
        : {}

  const filterLabelsConfig: Record<string, string> | undefined =
    activeTab === 'pending'
      ? {
          Status: 'Forms status',
          Source: 'Request source',
        }
      : activeTab === 'completed'
        ? { Source: 'Request source' }
        : undefined

  const pendingSourceCount = scopedPendingData.filter(
    (entry) => !hasAllFormsCompleted(entry),
  ).length
  const pendingListNarrowed =
    searchQuery.trim().length > 0 ||
    (selectedFilters.Status?.length ?? 0) > 0 ||
    (selectedFilters.Source?.length ?? 0) > 0
  const pendingNoRecordsEmpty =
    !pendingFormsLoading &&
    filteredPendingData.length === 0 &&
    pendingSourceCount === 0
  const completedNoRecordsEmpty =
    !pendingFormsLoading &&
    filteredCompletedData.length === 0 &&
    completedEntries.length === 0

  const showContentSkeleton =
    viewMode === 'dashboard' &&
    (isTabSkeleton ||
      (isPageReady && activeTab === 'pending' && pendingFormsLoading) ||
      (isPageReady && activeTab === 'completed' && pendingFormsLoading) ||
      (isPageReady && activeTab === 'send' && sendFormsLoading))

  const showCreateFormOnEmpty =
    (activeTab === 'pending' && pendingNoRecordsEmpty) ||
    (activeTab === 'completed' && completedNoRecordsEmpty) ||
    (activeTab === 'send' && sendNoRecordsEmpty)

  const renderEmptyState = (): React.ReactElement => {
    if (activeTab === 'pending') {
      return (
        <EmptyState
          className="form-builder-empty"
          iconClass="mgc mgc_document_fill"
          title="No pending forms found"
          description={
            pendingListNarrowed && pendingSourceCount > 0
              ? 'No pending forms match your search.'
              : "You don't have any pending forms on record yet."
          }
          actionLabel={pendingNoRecordsEmpty ? 'Create form' : undefined}
          onAction={pendingNoRecordsEmpty ? handleCreateForm : undefined}
        />
      )
    }

    return (
      <EmptyState
        className="form-builder-empty"
        iconClass="mgc mgc_document_fill"
        title="No completed forms found"
        description={
          searchQuery.trim().length > 0 && completedEntries.length > 0
            ? 'No completed forms match your search.'
            : "You don't have any completed forms on record yet."
        }
        actionLabel={completedNoRecordsEmpty ? 'Create form' : undefined}
        onAction={completedNoRecordsEmpty ? handleCreateForm : undefined}
      />
    )
  }

  const dashboardContent = (
    <div className={`form-builder-page${isPageReady ? ' is-ready' : ''}`}>
      {viewMode === 'dashboard' && (
        <div className="form-builder-header">
          <div className="form-builder-header-tabs">
            <Tabs
              items={TAB_ITEMS}
              value={activeTab}
              onChange={handleTabChange}
              className="form-builder-tabs"
            />
            {SHOW_FORM_BUILDER_KIOSK_LINK && (
              <button
                type="button"
                className="form-builder-kiosk-link"
                title="Open Patient Forms Access (Kiosk) in new window"
                onClick={() => {
                  openKioskWindow()
                }}
              >
                Open kiosk
              </button>
            )}
          </div>
          <div className="form-builder-header-actions">
            <SearchWithFilter
              className="form-builder-search"
              placeholder="Search"
              ariaLabel="Search forms"
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
              filters={filtersConfig}
              filterLabels={filterLabelsConfig}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onReset={clearAllFilters}
              showFilter={Object.keys(filtersConfig).length > 0}
              showResetButton={Object.keys(filtersConfig).length > 0}
            />
            <Button
              variant="secondary"
              size="md"
              type="button"
              onClick={() => setShowSendForm(true)}
            >
              Send form
            </Button>
            <Button
              variant="primary"
              size="md"
              type="button"
              className="create-form-button"
              onClick={handleCreateForm}
              disabled={showCreateFormOnEmpty}
            >
              Create new form
            </Button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <SendPatientFormsModal
        show={showSendForm}
        onHide={() => setShowSendForm(false)}
        patient={selectedStorePatient}
        onFormsAssigned={handleFormsAssigned}
      />
      <div className="form-builder-content-row">
        <div className="form-builder-content-col">
          {viewMode === 'patientView' && selectedPatient && (
            <PatientFormsView
              patientId={selectedPatient.patientId}
              patientName={selectedPatient.patientName}
              onBackToDashboard={() =>
                goBackToDashboard({ tab: subviewReturnTab })
              }
            />
          )}
          {viewMode === 'completedDetail' && completedDetailPayload && (
            <CompletedFormsView
              payload={completedDetailPayload}
              onBackToDashboard={() =>
                goBackToDashboard({ tab: subviewReturnTab })
              }
            />
          )}
          {viewMode === 'dashboard' && (
            <>
              {showContentSkeleton ? <FormBuilderSkeleton /> : null}
              <div
                className="form-builder-tab-panel"
                hidden={showContentSkeleton}
                aria-hidden={showContentSkeleton}
              >
                {activeTab === 'pending' &&
                  (filteredPendingData.length > 0 ? (
                    <PendingFormsTable
                      data={filteredPendingData}
                      onOpenPatientForms={openPatientForms}
                      onOpenCompletedDetail={openCompletedDetail}
                      initialExpandedPatientId={pendingExpandedPatientId}
                    />
                  ) : pendingFormsLoading ? null : (
                    renderEmptyState()
                  ))}
                {activeTab === 'send' && (
                  <SendFormsTab
                    selectedPatientId={selectedPatientId ?? undefined}
                    searchQuery={searchQuery}
                    onFormsAssigned={handleFormsAssigned}
                    onCreateForm={handleCreateForm}
                    onEmptyRecordsChange={setSendNoRecordsEmpty}
                    onLoadingChange={setSendFormsLoading}
                    suppressGlobalLoading
                  />
                )}
                {activeTab === 'completed' &&
                  (filteredCompletedData.length > 0 ? (
                    <CompletedFormsTable
                      data={filteredCompletedData}
                      onOpenPatientForms={openPatientForms}
                      initialSelectedPatientId={completedSelectedPatientId}
                    />
                  ) : pendingFormsLoading ? null : (
                    renderEmptyState()
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <Routes>
      <Route index element={dashboardContent} />
      <Route
        path="templates"
        element={
          <Templates onBackToDashboard={() => navigate('/form-builder')} />
        }
      />
      <Route
        path="create"
        element={<CreateForm onFormsAssigned={handleFormsAssigned} />}
      />
      <Route
        path="edit/:templateId"
        element={<CreateForm onFormsAssigned={handleFormsAssigned} />}
      />
      <Route
        path="view/:templateId"
        element={<CreateForm onFormsAssigned={handleFormsAssigned} />}
      />
    </Routes>
  )
}

export default FormBuilder
